// Supported languages for the site. English is always the unprefixed default;
// every other entry gets a "/<code>" URL prefix. Add future languages here only —
// no route changes needed elsewhere.
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "tr", label: "Türkçe" },
  { code: "ur", label: "اردو", rtl: true },
  { code: "fr", label: "Français" },
  { code: "ru", label: "Русский" },
  { code: "pt", label: "Português" },
  { code: "es", label: "Español" },
  { code: "ar", label: "العربية", rtl: true },
  { code: "ps", label: "پښتو", rtl: true },
  { code: "prs", label: "دری", rtl: true },
  { code: "fa", label: "فارسی", rtl: true },
  { code: "zh", label: "简体中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
];

export const DEFAULT_LANGUAGE = "en";

// Canonical origin for absolute URLs — hreflang and canonical <link> tags
// must use absolute URLs, not relative ones.
export const SITE_URL = "https://www.magalaxyintl.com";

// hreflang values follow their own standard (ISO 639-1 language + optional
// ISO 3166-1 region subtag) — separate from the routing codes above, which
// only need to be valid URL segments and i18next resource keys. Only Dari
// needs remapping: "prs" is an ISO 639-3 code and Google does not accept it
// as an hreflang value. The recognized way to target Dari (Afghanistan) is
// the Persian macrolanguage code "fa" with the "AF" region subtag — distinct
// from our separate "fa" entry above, which targets general/Iranian Persian.
const HREFLANG_OVERRIDES = { prs: "fa-AF" };

export function getHreflangCode(langCode) {
  return HREFLANG_OVERRIDES[langCode] || langCode;
}

// True for languages that read right-to-left (Urdu, Arabic). Drives
// document.documentElement.dir via applyDocumentDirection() below.
export function isRtlLanguage(code) {
  const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  return Boolean(lang && lang.rtl);
}

// Sets <html dir> and <html lang> for the given language code. Shared by
// LanguageWrapper (URL-driven, covers the "/" and "/:lang" branches) and
// TradePortalPage's browser-detection effect (covers the standalone /portal
// route, which LanguageWrapper never touches) — kept in one place so the two
// call sites can't drift into different dir-setting logic.
//
// Known temporary artifact: until ur/ar have real translations, forcing
// dir="rtl" here wraps plain English fallback paragraphs in an RTL
// container, and the bidi algorithm visually moves trailing punctuation
// (e.g. a sentence-ending period) to the front of the line. This is not a
// bug to fix — it's a side effect of English text sitting inside a forced
// RTL context, and it resolves on its own once real Urdu/Arabic (genuinely
// RTL) content replaces that fallback text.
export function applyDocumentDirection(langCode) {
  document.documentElement.dir = isRtlLanguage(langCode) ? "rtl" : "ltr";
  document.documentElement.lang = langCode;
}

// Routes mounted outside the "/" and "/:lang" branches in App.js — they have
// no localized URL counterpart (e.g. no "/tr/portal"), so LanguageRedirect
// must never try to send a visitor there.
export const STANDALONE_ROUTES = ["/admin", "/portal"];

// Shared by i18n.js (detector init lookup), LanguageSwitcher (writes on
// explicit choice), and LanguageRedirect (reads to skip auto-detection).
export const LANGUAGE_STORAGE_KEY = "userSelectedLanguage";

export function isSupportedLanguage(code) {
  return SUPPORTED_LANGUAGES.some((lang) => lang.code === code);
}

// "" for English, "/tr" etc. for everything else.
export function getLangPrefix(langCode) {
  return langCode && langCode !== DEFAULT_LANGUAGE ? `/${langCode}` : "";
}

// Builds a language-prefixed path from a bare (English) path, e.g.
// buildLocalizedPath("/about", "tr") -> "/tr/about"
// buildLocalizedPath("/about", "en") -> "/about"
export function buildLocalizedPath(path, langCode) {
  const prefix = getLangPrefix(langCode);
  if (path === "/") return prefix || "/";
  return `${prefix}${path}`;
}

// Strips a known language prefix from a pathname, e.g. "/tr/about" -> "/about".
// Returns the pathname unchanged if it has no recognized language prefix.
export function stripLangPrefix(pathname) {
  const match = pathname.match(/^\/([a-zA-Z-]+)(\/.*|$)/);
  if (match && isSupportedLanguage(match[1]) && match[1] !== DEFAULT_LANGUAGE) {
    return match[2] || "/";
  }
  return pathname;
}
